import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Headphones, Play, Radio, Satellite, Shield, Terminal,
  Eye, Clock, Map, Zap, Lock, Volume2, VolumeX, ChevronRight, Compass,
  AlertTriangle, Star, Check, Crosshair
} from 'lucide-react';
import useTTS from '../lib/useTTS';
import useHarperNarration from '../lib/useHarperNarration';

/* ─── zone data (static, matches backend seed) ─── */
const ZONES = [
  { number: 1, name: 'The Town & Flats', classification: 'Dense Urban Zone', threat: 'low_to_moderate', distance: '1.2 km', rooms: 11, points: 85, desc: 'Once-thriving settlement now fallen into disrepair. Concrete structures crumble at the edges. Pitch-black Rigel V night.' },
  { number: 2, name: 'Canal & Bridge Route', classification: 'Waterway Corridor', threat: 'moderate', distance: '0.8 km', rooms: 5, points: 35, desc: 'Wide industrial canal cuts through the landscape. Federation-monitored bridge or the derelict mill offer alternative crossings.' },
  { number: 3, name: 'The Minefield', classification: 'Cratered Exclusion Zone', threat: 'extreme', distance: '1.5 km', rooms: 5, points: 75, desc: 'Hellscape of craters and twisted metal. Unexploded ordnance everywhere. Every step could be fatal.' },
  { number: 4, name: 'Huts & Rural Route', classification: 'Gnarled Path', threat: 'low_to_moderate', distance: '1.1 km', rooms: 5, points: 85, desc: 'Sparse vegetation and small dwellings. Riverside Bungalows cluster near a tributary. Respite zone between danger zones.' },
  { number: 5, name: 'Industrial Approach', classification: 'High-Security Perimeter', threat: 'high', distance: '0.9 km', rooms: 7, points: 110, desc: 'Outer defensive ring of the Warehouse Complex. Razor-wire fences, floodlights, patrol routes, security towers.' },
  { number: 6, name: 'Warehouse & Complex', classification: 'Restricted Core Facility', threat: 'extreme', distance: '0.4 km', rooms: 6, points: 145, desc: 'Heart of the Federation operation. Massive warehouses, scientific personnel, the Doomsday Machine.' },
];

const TIERS = [
  {
    id: 'standard', name: 'STANDARD', price: '1.99', tagline: 'The full story, gentle nudges.',
    desc: 'Hints when you\'re stuck. Best for first-time players.',
    features: ['Full 39-room story', 'Hint command available', 'Checkpoint saves', 'Forgiving deaths', 'Voice narration'],
    accent: 'green',
  },
  {
    id: 'classic', name: 'CLASSIC', price: '3.99', tagline: '1987 spirit.',
    desc: 'No hints, no hand-holding. Figure it out — or don\'t.',
    features: ['Full 39-room story', 'No hints ever', 'No saves — one run', 'Instant death minefield', 'Voice narration'],
    accent: 'amber', popular: true,
  },
  {
    id: 'immersive', name: 'IMMERSIVE', price: '5.99', tagline: 'Living world.',
    desc: 'Your choices ripple. The hardest, richest run.',
    features: ['Full 39-room story', 'No hints or saves', 'Sentries remember you', 'Real-time pressure', 'Consequence ripple system', 'Voice narration'],
    accent: 'red',
  },
];

const THREAT_ACCENT = {
  low_to_moderate: { color: 'var(--hud-green-dim)', label: 'LOW-MOD' },
  moderate: { color: 'var(--hud-amber)', label: 'MODERATE' },
  high: { color: 'var(--hud-amber)', label: 'HIGH' },
  extreme: { color: 'var(--hud-red)', label: 'EXTREME' },
};

/* ─── typing animation hook ─── */
function useTypingText(text, speed = 38, startDelay = 800) {
  const [displayed, setDisplayed] = useState('');
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const delayTimer = setTimeout(() => setStarted(true), startDelay);
    return () => clearTimeout(delayTimer);
  }, [startDelay]);

  useEffect(() => {
    if (!started) return;
    if (displayed.length >= text.length) return;
    const timer = setTimeout(() => setDisplayed(text.slice(0, displayed.length + 1)), speed);
    return () => clearTimeout(timer);
  }, [displayed, started, text, speed]);

  return displayed;
}

/* ─── main component ─── */
// Section text for TTS read-aloud (keyed by section id)
const SECTION_SPEECH = {
  hero: `Rigel's Revenge. A sci-fi text adventure. Suit integrity unstable. Patrol signatures detected. If anyone hears this, I am moving toward the wreck.`,
  world: `The Crash. Harper's dropship was not lost to engine failure. A deliberate strike tore the sky open. The android pilot is silent. Elliot's transponder has stopped answering. Harper is alone with a burning wreck and a closing patrol. The Mission. Cross 5.9 kilometres of hostile terrain — abandoned towns, minefields, guarded bridges, and the Federation's restricted core — to find your crewmate and shut down the Doomsday Machine before it's too late.`,
  gameplay: `Audio first gameplay. Every scene is fully narrated. Every choice has an audio cue. Playable with your eyes closed. Night vision goggles let you see the world in green phosphor, or play unaided in the dark.`,
  zones: `Six zones. 5.9 kilometres of hostile terrain between Harper and the Doomsday Machine. From the abandoned town to the restricted warehouse complex.`,
  pricing: `Three tiers. Standard at 1 pound 99. Classic at 3 pounds 99. Immersive at 5 pounds 99. Zone 1 is free.`,
  cta: `Begin transmission. The wreck is cooling. The patrol is closing. Harper is waiting for you.`,
};

export default function Landing() {
  const [time, setTime] = useState('');
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionRefs = useRef({});
  const spokenSections = useRef(new Set());
  const { ttsEnabled, toggleTTS, speak, stop: stopTTS, speaking: ttsSpeaking, supported: ttsSupported } = useTTS();
  const harper = useHarperNarration();

  /* clock */
  useEffect(() => {
    const tick = () => {
      const d = new Date();
      setTime(`${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`);
    };
    tick();
    const clockTimer = setInterval(tick, 1000);
    return () => clearInterval(clockTimer);
  }, []);

  /* intersection observer for scroll-triggered animations */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.dataset.section]));
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -60px 0px' }
    );

    Object.values(sectionRefs.current).forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const registerSection = (id) => (el) => {
    if (el) {
      el.dataset.section = id;
      sectionRefs.current[id] = el;
    }
  };

  const isVisible = (id) => visibleSections.has(id);

  // TTS: read section text when it scrolls into view (browser fallback)
  useEffect(() => {
    if (!ttsEnabled || harper.narrationEnabled) return; // Harper narration takes priority
    for (const section of visibleSections) {
      if (!spokenSections.current.has(section) && SECTION_SPEECH[section]) {
        spokenSections.current.add(section);
        speak(SECTION_SPEECH[section], { rate: 0.9 });
      }
    }
  }, [ttsEnabled, harper.narrationEnabled, visibleSections, speak]);

  // Harper Kael narration: play pre-recorded audio when sections scroll into view
  useEffect(() => {
    if (!harper.narrationEnabled) return;
    const ordered = ['hero', 'world', 'gameplay', 'zones', 'pricing', 'cta'];
    for (const section of ordered) {
      if (visibleSections.has(section)) {
        harper.playSection(section);
        break; // only play the first visible unplayed section
      }
    }
  }, [harper.narrationEnabled, visibleSections, harper.playSection]);

  const typedTransmission = useTypingText(
    '"Suit integrity unstable. Patrol signatures detected. If anyone hears this, I am moving toward the wreck."',
    32,
    1200
  );

  return (
    <div className="rr-landing-shell rr-landing-cinematic rr-landing-plain rr-landing-awesome">
      {/* video background */}
      <div
        className="rr-landing-bg rr-landing-bg-cinematic rr-landing-bg-plain rr-landing-video-bg"
        style={{ backgroundImage: "url('/assets/scenes/landing-user-video-poster.jpg'), url('/assets/scenes/landing-hero.png')" }}
        aria-hidden="true"
      >
        <video
          className="rr-landing-bg-video"
          autoPlay muted loop playsInline preload="auto"
          poster="/assets/scenes/landing-user-video-poster.jpg"
        >
          <source src="/assets/scenes/landing-user-video.webm" type="video/webm" />
          <source src="/assets/scenes/landing-user-video.mp4" type="video/mp4" />
        </video>
      </div>
      <div className="rr-landing-atmosphere" aria-hidden="true" />
      <div className="rr-landing-flare rr-landing-flare-plain" aria-hidden="true" />
      <div className="rr-awesome-orbit" aria-hidden="true" />

      {/* ═══════ NAV ═══════ */}
      <header className="rr-landing-nav rr-landing-nav-plain rr-awesome-nav">
        <Link to="/" className="rr-brand rr-brand-cinematic rr-brand-plain rr-awesome-brand" aria-label="Rigel's Revenge home">
          <span className="rr-status-dot" />
          <span className="rr-brand-mark">RR</span>
          <span>RIGEL'S REVENGE</span>
        </Link>
        <nav className="rr-awesome-links" aria-label="Landing page navigation">
          <a href="#world">World</a>
          <a href="#gameplay">Play</a>
          <a href="#zones">Zones</a>
          <a href="#pricing">Pricing</a>
          <Link to="/map">Map</Link>
        </nav>
        <button
          className={`rr-tts-toggle ${harper.narrationEnabled ? 'rr-tts-active rr-harper-active' : ''}`}
          onClick={harper.toggle}
          title={harper.narrationEnabled ? 'Harper narration on — click to disable' : 'Enable Harper Kael narration'}
          aria-label={harper.narrationEnabled ? 'Disable Harper narration' : 'Enable Harper Kael narration'}
        >
          {harper.narrationEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
          <span>{harper.playing ? 'Harper Speaking…' : harper.narrationEnabled ? 'Harper On' : 'Harper Narrate'}</span>
        </button>
        <div className="rr-nav-meta rr-nav-meta-plain rr-awesome-signal">
          <Radio size={14} /> SIGNAL OPEN <span>{time || '--:--:--'}</span>
        </div>
      </header>

      {/* ═══════ HERO ═══════ */}
      <main className="rr-awesome-stage" aria-labelledby="landing-title">
        <section className="rr-awesome-copy">
          <p className="rr-kicker rr-plain-kicker rr-awesome-kicker">
            <Satellite size={15} /> DISTRESS SIGNAL · RIGEL 5 · IMPACT SECTOR RV-7A
          </p>
          <h1 id="landing-title" className="rr-plain-title rr-awesome-title" aria-label="Rigel's Revenge">
            <span className="rr-title-main">Rigel's</span>
            <span className="rr-title-revenge">Revenge</span>
          </h1>
          <p className="rr-plain-tagline rr-awesome-tagline">
            Harper is alive. The ship is burning. Somewhere beyond the smoke, his crewmate is still transmitting.
            Step into a dark sci-fi survival console where every command decides what the planet reveals next.
          </p>

          <div className="rr-hero-actions rr-plain-actions rr-awesome-actions">
            <Link to="/play" className="rr-cta rr-cta-primary rr-cta-plain rr-awesome-primary">
              <Play size={18} /> Begin Transmission <ArrowRight size={18} />
            </Link>
            <a href="#gameplay" className="rr-cta rr-cta-plain rr-awesome-secondary">
              <Eye size={18} /> See How It Works
            </a>
          </div>

          <div className="rr-awesome-brief" aria-label="Opening transmission excerpt">
            <Headphones size={18} />
            <p>{typedTransmission}<span className="rr-cursor-blink">|</span></p>
          </div>
        </section>

        {/* hero right — telemetry card */}
        <div className="rr-awesome-card rr-hero-telemetry-card">
          <div className="rr-awesome-card-top">
            <span><Crosshair size={14} /> MISSION BRIEF</span>
            <strong><AlertTriangle size={12} /> HOSTILE</strong>
          </div>
          <div className="rr-awesome-telemetry">
            <div><span>ZONES</span><strong>6</strong></div>
            <div><span>ROOMS</span><strong>39</strong></div>
            <div><span>ITEMS</span><strong>103</strong></div>
            <div><span>DISTANCE</span><strong>5.9 km</strong></div>
          </div>
          <div className="rr-awesome-sequence">
            <div className="rr-awesome-sequence-row"><i>01</i><span>Survive the crash</span><ChevronRight size={14} /></div>
            <div className="rr-awesome-sequence-row"><i>02</i><span>Find your crewmate</span><ChevronRight size={14} /></div>
            <div className="rr-awesome-sequence-row"><i>03</i><span>Reach the Complex</span><ChevronRight size={14} /></div>
            <div className="rr-awesome-sequence-row"><i>04</i><span>Stop the Machine</span><Lock size={14} /></div>
          </div>
          <div className="rr-awesome-card-bottom">
            <div>
              <Shield size={16} style={{ color: 'var(--rr-green)', flexShrink: 0 }} />
              <span>Zone 1 free. No credit card required.</span>
            </div>
          </div>
        </div>
      </main>

      {/* ═══════ WORLD OVERVIEW ═══════ */}
      <section
        id="world"
        ref={registerSection('world')}
        className={`rr-section rr-section-world ${isVisible('world') ? 'rr-visible' : ''}`}
      >
        <div className="rr-section-inner">
          <SectionHeader
            icon={<Compass size={16} />}
            label="PLANETARY DOSSIER"
            title="Welcome to Rigel V"
            subtitle="Zeta Cephei System · Desert / Industrial · Federation Governance"
          />

          <div className="rr-world-grid">
            <div className="rr-world-card">
              <div className="rr-world-card-icon"><Satellite size={28} /></div>
              <h3>The Planet</h3>
              <p>
                A Federation-controlled desert world in the Zeta Cephei system.
                Perpetual night. Industrial infrastructure falling apart under
                sand and silence. Something went wrong here before Harper arrived.
              </p>
            </div>
            <div className="rr-world-card">
              <div className="rr-world-card-icon"><AlertTriangle size={28} /></div>
              <h3>The Crash</h3>
              <p>
                Harper's dropship was not lost to engine failure. A deliberate
                strike tore the sky open. The android pilot is silent. Elliot's
                transponder has stopped answering. Harper is alone with a burning wreck and a closing patrol.
              </p>
            </div>
            <div className="rr-world-card">
              <div className="rr-world-card-icon"><Crosshair size={28} /></div>
              <h3>The Mission</h3>
              <p>
                Cross 5.9 kilometres of hostile terrain — abandoned towns,
                minefields, guarded bridges, and the Federation's restricted
                core — to find your crewmate and shut down the Doomsday Machine
                before it's too late.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ GAMEPLAY ═══════ */}
      <section
        id="gameplay"
        ref={registerSection('gameplay')}
        className={`rr-section rr-section-gameplay ${isVisible('gameplay') ? 'rr-visible' : ''}`}
      >
        <div className="rr-section-inner">
          <SectionHeader
            icon={<Terminal size={16} />}
            label="HOW YOU PLAY"
            title="A Text Adventure. Reimagined."
            subtitle="Type commands. Make choices. Survive the consequences."
          />

          <div className="rr-gameplay-demo">
            <div className="rr-demo-console">
              <div className="rr-demo-header">
                <span className="rr-status-dot" style={{ width: 8, height: 8 }} />
                <span>HARPER CONSOLE · BURNING CRASH SITE</span>
              </div>
              <div className="rr-demo-body">
                <p className="rr-demo-narration">
                  Consciousness returns as a thin red line: heat on your left side,
                  grit packed behind your teeth, and the brutal weight of Rigel 5
                  pressing your ribs into broken stone. Harper tries to blink. The darkness does not change.
                </p>
                <div className="rr-demo-log">
                  <div className="rr-demo-cmd">&gt; search rubble</div>
                  <div className="rr-demo-response">
                    You sweep the nearest debris by touch and find a cracked hardcase
                    trapped beneath cargo webbing. Inside are Harper's NVG night-vision
                    goggles: left hinge bent, casing scored, power cell still warm.
                  </div>
                  <div className="rr-demo-cmd">&gt; wear goggles</div>
                  <div className="rr-demo-response">
                    Harper drags the damaged NVG goggles into place. The bent frame
                    bites into a cut above the cheekbone; the lenses stay black,
                    waiting for power.
                  </div>
                  <div className="rr-demo-cmd">&gt; activate goggles</div>
                  <div className="rr-demo-response rr-demo-response-green">
                    The goggles cough alive and flood Harper's skull with fractured
                    green light. Wreckage resolves out of the black: burning hull ribs,
                    dragged cargo, glassed desert stone…
                  </div>
                </div>
                <div className="rr-demo-input">
                  <span>Harper Command</span>
                  <div className="rr-demo-input-field">
                    <span className="rr-demo-cursor">east</span>
                    <span className="rr-cursor-blink">|</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="rr-gameplay-features">
              <FeatureCard icon={<Terminal size={20} />} title="Command Parser" desc="Type natural commands. The game understands exploration, combat, puzzle-solving, and everything in between." />
              <FeatureCard icon={<Eye size={20} />} title="360° Scene Viewer" desc="Pop out an immersive panoramic view of every room. Drag to look around. NVG filter reveals hidden details." />
              <FeatureCard icon={<Volume2 size={20} />} title="AI Voice Narration" desc="Every scene narrated aloud by AI. Listen to the story unfold as you play — pause, replay, or read instead." />
              <FeatureCard icon={<Clock size={20} />} title="Real-Time Pressure" desc="The patrol timer is always running. Hesitate too long and the search team closes in. Decisions have deadlines." />
              <FeatureCard icon={<Zap size={20} />} title="Consequence System" desc="Actions ripple forward. Items you miss, NPCs you ignore, doors you leave open — the world remembers." />
              <FeatureCard icon={<Map size={20} />} title="39 Rooms to Explore" desc="Six distinct zones across 5.9 km of hostile terrain. Every room hand-crafted with its own narration and puzzles." />
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ ZONES ═══════ */}
      <section
        id="zones"
        ref={registerSection('zones')}
        className={`rr-section rr-section-zones ${isVisible('zones') ? 'rr-visible' : ''}`}
      >
        <div className="rr-section-inner">
          <SectionHeader
            icon={<Map size={16} />}
            label="SECTOR SURVEY"
            title="Six Zones. One Escape Route."
            subtitle="5.9 km of hostile terrain between Harper and the Doomsday Machine."
          />

          <div className="rr-zones-grid">
            {ZONES.map((z) => {
              const threat = THREAT_ACCENT[z.threat] || THREAT_ACCENT.moderate;
              return (
                <div key={z.number} className="rr-zone-card">
                  <div className="rr-zone-card-header">
                    <span className="rr-zone-number" style={{ color: threat.color }}>{String(z.number).padStart(2, '0')}</span>
                    <div>
                      <h3>{z.name}</h3>
                      <span className="rr-zone-class">{z.classification}</span>
                    </div>
                  </div>
                  <p>{z.desc}</p>
                  <div className="rr-zone-stats">
                    <span className="hud-chip">{z.distance}</span>
                    <span className="hud-chip" style={{ color: threat.color, borderColor: threat.color }}>
                      <AlertTriangle size={10} /> {threat.label}
                    </span>
                    <span className="hud-chip">{z.rooms} ROOMS</span>
                    <span className="hud-chip hud-chip-green">
                      <Star size={10} /> {z.points} PTS
                    </span>
                  </div>
                  {z.number === 1 && (
                    <div className="rr-zone-free-badge">
                      <Shield size={12} /> FREE TO PLAY
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="rr-zones-cta">
            <Link to="/map" className="rr-cta rr-cta-plain" style={{ borderColor: 'rgba(117,255,134,0.44)' }}>
              <Map size={18} /> Open Full Map Console <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════ PRICING ═══════ */}
      <section
        id="pricing"
        ref={registerSection('pricing')}
        className={`rr-section rr-section-pricing ${isVisible('pricing') ? 'rr-visible' : ''}`}
      >
        <div className="rr-section-inner">
          <SectionHeader
            icon={<Shield size={16} />}
            label="DIFFICULTY TIERS"
            title="Choose Your Survival Mode"
            subtitle="Zone 1 is free. Pick a tier to unlock the full 39-room campaign."
          />

          <div className="rr-pricing-grid">
            {TIERS.map((tier) => (
              <div
                key={tier.id}
                className={`rr-pricing-card rr-pricing-${tier.accent} ${tier.popular ? 'rr-pricing-popular' : ''}`}
              >
                {tier.popular && <div className="rr-popular-badge">MOST POPULAR</div>}
                <div className="rr-pricing-header">
                  <h3>{tier.name}</h3>
                  <p className="rr-pricing-tagline">{tier.tagline}</p>
                </div>
                <div className="rr-pricing-price">
                  <span className="rr-pricing-currency">£</span>
                  <span className="rr-pricing-amount">{tier.price}</span>
                  <span className="rr-pricing-period">/month</span>
                </div>
                <p className="rr-pricing-desc">{tier.desc}</p>
                <ul className="rr-pricing-features">
                  {tier.features.map((f) => (
                    <li key={f}><Check size={14} /> {f}</li>
                  ))}
                </ul>
                <Link to="/play" className={`rr-cta rr-cta-plain rr-pricing-cta rr-pricing-cta-${tier.accent}`}>
                  <Play size={16} /> Start {tier.name} <ArrowRight size={16} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ NARRATIVE / CTA ═══════ */}
      <section
        ref={registerSection('cta')}
        className={`rr-section rr-section-final ${isVisible('cta') ? 'rr-visible' : ''}`}
      >
        <div className="rr-section-inner rr-final-inner">
          <div className="rr-final-transmission">
            <Headphones size={24} style={{ color: 'var(--rr-green)', flexShrink: 0 }} />
            <blockquote>
              "Consciousness returns as a thin red line: heat on your left side,
              grit packed behind your teeth, and the brutal weight of Rigel 5
              pressing your ribs into broken stone. Harper tries to blink.
              The darkness does not change."
            </blockquote>
          </div>

          <h2 className="rr-final-title font-display">
            The Ship Is Burning.<br />
            <span>Harper Needs You.</span>
          </h2>

          <div className="rr-hero-actions" style={{ justifyContent: 'center' }}>
            <Link to="/play" className="rr-cta rr-cta-primary rr-cta-plain rr-awesome-primary" style={{ fontSize: '1rem', padding: '18px 28px' }}>
              <Play size={20} /> Begin Transmission <ArrowRight size={20} />
            </Link>
          </div>

          <p className="rr-final-sub">
            Zone 1 free · No account required · Play in your browser
          </p>
        </div>
      </section>

      {/* ═══════ FOOTER ═══════ */}
      <footer className="rr-footer">
        <div className="rr-footer-inner">
          <div className="rr-footer-brand">
            <span className="rr-status-dot" style={{ width: 8, height: 8 }} />
            <span className="font-display" style={{ fontSize: '0.82rem', letterSpacing: '0.16em' }}>RIGEL'S REVENGE</span>
          </div>
          <div className="rr-footer-links">
            <Link to="/play">Play</Link>
            <Link to="/map">Map Console</Link>
            <a href="#world">World</a>
            <a href="#pricing">Pricing</a>
          </div>
          <div className="rr-footer-meta">
            <span>RIGEL V · ZETA CEPHEI SYSTEM · SURVEY 04.17.78</span>
            <span>© {new Date().getFullYear()} Rigel's Revenge</span>
          </div>
        </div>
      </footer>
    </div>
  );
}


/* ─── sub-components ─── */
function SectionHeader({ icon, label, title, subtitle }) {
  return (
    <div className="rr-section-header">
      <p className="rr-section-label">
        {icon} {label}
      </p>
      <h2 className="rr-section-title font-display">{title}</h2>
      {subtitle && <p className="rr-section-subtitle">{subtitle}</p>}
    </div>
  );
}

function FeatureCard({ icon, title, desc }) {
  return (
    <div className="rr-feature-card">
      <div className="rr-feature-icon">{icon}</div>
      <div>
        <h4>{title}</h4>
        <p>{desc}</p>
      </div>
    </div>
  );
}
